import { BrainCircuit, Settings, MessageCircle, Laptop, Smartphone, Cloud, Bot } from 'lucide-react';
import { motion } from 'framer-motion';

const services = [
  {
    icon: <Laptop size={32} />,
    title: 'Full Stack Development',
    description: 'End-to-end web application development with modern technologies. React, Node.js, Python, and cloud deployment.',
    features: ['React & Next.js', 'Node.js & Python', 'Database Design', 'API Development']
  },
  {
    icon: <Smartphone size={32} />,
    title: 'Mobile Development',
    description: 'Native and cross-platform mobile apps for iOS and Android using Swift, Kotlin, and Flutter.',
    features: ['iOS & Android', 'Flutter & React Native', 'App Store Deployment', 'UI/UX Design']
  },
  {
    icon: <Cloud size={32} />,
    title: 'DevOps & Cloud',
    description: 'Scalable infrastructure, CI/CD pipelines, and cloud optimization for your applications.',
    features: ['AWS & Google Cloud', 'Docker & Kubernetes', 'CI/CD Pipelines', 'Infrastructure as Code']
  },
  {
    icon: <BrainCircuit size={32} />,
    title: 'AI & Machine Learning',
    description: 'Intelligent solutions using computer vision, NLP, and deep learning technologies.',
    features: ['Computer Vision', 'Natural Language Processing', 'Deep Learning', 'AI Integration']
  },
];

const Services = () => {
  return (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Our <span className="text-gradient">Services</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/80 max-w-3xl mx-auto"
          >
            From idea to deployment, we provide comprehensive development services
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="lovable-card p-8 hover:bg-gray-800/90 transition-all group"
            >
              <div className="flex items-start gap-6">
                <div className="p-3 bg-gradient-to-r from-blue-400 to-green-500 rounded-lg text-white group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-green-300 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-sm text-white/60">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="lovable-card p-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to start your project?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Let's discuss how we can help bring your ideas to life
            </p>
            <div className="flex gap-4 justify-center">
              <a
                href="https://calendly.com/sergii-kozyrev-kallisto/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="lovable-button"
              >
                Schedule a call
              </a>
              <a
                href="mailto:sergii.kozyrev@kallisto.dev"
                className="lovable-button-outline"
              >
                Send email
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;